import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, PanelRightOpen } from 'lucide-react';
import { InlineArea } from '../inline';
import { Avatar } from '../ui';
import { periodState, rangeLabel, toDay } from './dates';
import { sprintProgress } from './colors';
import { STATUS, STATUS_OPTIONS, STATUS_ORDER } from './objectives';

// Sous la frise : le sprint à regarder (celui en cours, ou celui qu'on a ouvert), sa vision et ses objectifs en trois
// colonnes. Le statut d'un objectif se change sur place ; le reste se fait dans le backlog.

const COLUMNS = [
  { key: 'todo', label: 'À faire', statuses: ['not_started'] },
  { key: 'doing', label: 'En cours', statuses: ['blocked', 'in_progress'] },
  { key: 'done', label: 'Terminé', statuses: ['done'] },
];

const STATE_CHIP = {
  current: 'bg-emerald-50 text-emerald-700',
  upcoming: 'bg-slate-100 text-slate-600',
  done: 'bg-slate-100 text-slate-500',
  undated: 'bg-amber-50 text-amber-700',
};

const LIMIT = { todo: 8, doing: 8, done: 3 };

const ObjectiveCard = ({ objective, owner, onStatus }) => {
  const [saving, setSaving] = useState(false);
  const status = STATUS[objective.status] || STATUS.not_started;
  const change = async (value) => {
    setSaving(true);
    try {
      await onStatus(objective.id, value);
    } catch {
      // erreur affichée dans le bandeau de la fiche
    } finally {
      setSaving(false);
    }
  };
  return (
    <li className={`rounded-xl border bg-white px-3 py-2 shadow-sm ${objective.status === 'blocked' ? 'border-rose-200' : 'border-slate-200/80'}`}>
      <p className={`text-[13px] leading-snug ${objective.status === 'done' ? 'text-slate-400 line-through' : 'text-slate-800'}`}>{objective.name}</p>
      <div className="mt-1.5 flex items-center gap-2">
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${status.dot}`} />
        <select value={objective.status || 'not_started'} disabled={saving} onChange={(e) => change(e.target.value)} aria-label="Statut"
          className={`min-w-0 cursor-pointer rounded-md border-0 bg-transparent py-0 pl-0 pr-5 text-[11px] font-medium focus:ring-1 focus:ring-indigo-200 ${objective.status === 'blocked' ? 'text-rose-600' : 'text-slate-500'}`}>
          {STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        <span className="ml-auto flex items-center gap-1.5">
          {objective.effort ? <span className="text-[11px] tabular-nums text-slate-400">{objective.effort} pt</span> : null}
          {owner && <Avatar name={owner.name} picture={owner.picture} size="h-5 w-5" className="ring-1" />}
        </span>
      </div>
    </li>
  );
};

const SprintBoard = ({
  sprint, sprints, parallel = [], colors, today, objectives, employees, color, readOnly, onBrowse, onOpen, onPatchSprint,
  onStatus, openBacklog, note,
}) => {
  const [unfolded, setUnfolded] = useState({});
  const items = useMemo(() => objectives.filter((o) => o.sprint_id === sprint.id)
    .sort((a, b) => (STATUS_ORDER[a.status] ?? 2) - (STATUS_ORDER[b.status] ?? 2)), [objectives, sprint.id]);
  const owners = useMemo(() => new Map((employees || []).map((e) => [Number(e.id), e])), [employees]);
  const start = toDay(sprint.start_date);
  const end = toDay(sprint.end_date);
  const state = periodState(start, end, today);
  // Les flèches restent dans l'itération du sprint (les itérations parallèles ont leur onglet).
  const siblings = sprints.filter((s) => s.iteration.id === sprint.iteration.id);
  const index = siblings.findIndex((s) => s.id === sprint.id);
  const previous = index > 0 ? siblings[index - 1] : null;
  const next = index >= 0 && index < siblings.length - 1 ? siblings[index + 1] : null;
  const total = Number(sprint.total_tokens) || 0;
  const done = Number(sprint.tokens_completed) || 0;
  const when = state === 'current' ? `en cours · encore ${end - today} j`
    : state === 'upcoming' ? `à venir · dans ${start - today} j`
      : state === 'done' ? 'terminé' : 'dates à préciser';

  return (
    <section className="rounded-2xl border border-slate-200/80 bg-white shadow-sm">
      {parallel.length > 1 && (
        <div className="flex flex-wrap gap-1 border-b border-slate-100 px-4 pt-3 pb-2 sm:px-5">
          {parallel.map((other) => (
            <button key={other.id} type="button" onClick={() => onBrowse(other)}
              className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-semibold transition ${
                other.iteration.id === sprint.iteration.id ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'}`}>
              <span className={`h-2 w-2 rounded-full ${colors.get(other.iteration.id).dot}`} />
              {other.iteration.name} · {other.short}
            </button>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3 sm:px-5">
        <div className="flex items-center gap-0.5">
          <button type="button" onClick={() => previous && onBrowse(previous)} disabled={!previous} aria-label="Sprint précédent"
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"><ChevronLeft size={16} /></button>
          <button type="button" onClick={() => next && onBrowse(next)} disabled={!next} aria-label="Sprint suivant"
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"><ChevronRight size={16} /></button>
        </div>
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${color.dot}`} />
        <h3 className="text-sm font-semibold text-slate-900">Sprint {sprint.short}</h3>
        <span className="text-xs text-slate-400">{sprint.iteration.name}{start != null && end != null ? ` · ${rangeLabel(start, end)}` : ''}</span>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATE_CHIP[state]}`}>{when}</span>
        <span className="ml-auto flex items-center gap-1">
          <button type="button" onClick={openBacklog} className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-slate-500 hover:bg-slate-100 hover:text-indigo-700">
            Backlog <ExternalLink size={11} />
          </button>
          <button type="button" onClick={() => onOpen(sprint)} className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-slate-500 hover:bg-slate-100 hover:text-indigo-700">
            <PanelRightOpen size={12} /> Ouvrir
          </button>
        </span>
      </div>

      {note && <p className="px-4 pt-3 text-xs text-amber-700 sm:px-5">{note}</p>}
      <div className="grid gap-4 px-4 py-3 sm:px-5 md:grid-cols-[minmax(0,1fr)_200px]">
        <div className="min-w-0">
          <InlineArea value={sprint.vision} readOnly={readOnly} rows={1}
            placeholder={readOnly ? 'Pas de vision pour ce sprint.' : "Vision du sprint : ce qu'on veut avoir à la fin"}
            onSave={(vision) => onPatchSprint(sprint.id, { vision })} className="text-[15px] text-slate-800" />
        </div>
        <div className="self-center">
          <div className="mb-1 flex justify-between text-[11px] text-slate-500">
            <span>{items.filter((o) => o.status === 'done').length} / {items.length} objectifs</span>
            <span className="tabular-nums">{total ? `${done} / ${total} pt` : ''}</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
            <div className={`h-full rounded-full ${color.strong}`} style={{ width: `${Math.round(sprintProgress(sprint) * 100)}%` }} />
          </div>
        </div>
      </div>

      {items.length ? (
        <div className="grid gap-3 px-4 pb-4 sm:px-5 md:grid-cols-3">
          {COLUMNS.map((column) => {
            const list = items.filter((o) => column.statuses.includes(o.status || 'not_started'));
            const shown = unfolded[column.key] ? list : list.slice(0, LIMIT[column.key]);
            return (
              <div key={column.key} className="min-w-0 rounded-xl bg-slate-50 p-2">
                <p className="mb-1.5 flex items-center justify-between px-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {column.label} <span className="tabular-nums">{list.length}</span>
                </p>
                {list.length ? (
                  <ul className="space-y-1.5">
                    {shown.map((objective) => (
                      <ObjectiveCard key={objective.id} objective={objective} owner={owners.get(Number(objective.owner_id))} onStatus={onStatus} />
                    ))}
                    {list.length > shown.length && (
                      <li>
                        <button type="button" onClick={() => setUnfolded((prev) => ({ ...prev, [column.key]: true }))}
                          className="w-full rounded-lg px-2 py-1 text-left text-[11px] font-semibold text-slate-500 hover:bg-white hover:text-slate-800">
                          + {list.length - shown.length} de plus
                        </button>
                      </li>
                    )}
                  </ul>
                ) : (
                  <p className="px-1 pb-1 text-xs text-slate-300">—</p>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="px-4 pb-4 text-sm text-slate-400 sm:px-5">
          Aucun objectif dans ce sprint.{' '}
          <button type="button" onClick={openBacklog} className="font-semibold text-indigo-600 hover:underline">Les planifier depuis le backlog</button>
        </p>
      )}
    </section>
  );
};

export default SprintBoard;
